import { redirect } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/server";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, HardHat, Plus, Briefcase, Users } from "lucide-react";
import { SignOutButton } from "@/components/sign-out-button";
import { BottomNavWrapper } from "@/components/bottom-nav-wrapper";

export default async function DashboardPage() {
  const supabase = await createClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    redirect("/auth/login");
  }

  const { data: profile } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .single();

  const isContractor = profile?.type === "contractor";

  // Fetch stats
  let jobCount = 0;
  let matchCount = 0;

  if (isContractor) {
    const { data: company } = await supabase
      .from("companies")
      .select("id")
      .eq("profile_id", user.id)
      .single();

    if (company) {
      const { count } = await supabase
        .from("jobs")
        .select("*", { count: "exact", head: true })
        .eq("company_id", company.id)
        .eq("status", "active");
      jobCount = count || 0;

      const { count: mCount } = await supabase
        .from("matches")
        .select("*, jobs!inner(company_id)", { count: "exact", head: true })
        .eq("jobs.company_id", company.id)
        .in("status", ["applied", "suggested"]);
      matchCount = mCount || 0;
    }
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-screen-sm items-center justify-between px-4">
          <Link href="/dashboard" className="text-xl font-bold tracking-tight">
            RateRight
          </Link>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-sm py-1.5 px-3">
              {isContractor ? "Contractor" : "Worker"}
            </Badge>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 py-6">
        <h1 className="text-2xl font-bold">
          G&apos;day{profile?.name ? `, ${profile.name}` : ""}!
        </h1>
        <p className="mt-1 text-muted-foreground">
          {isContractor
            ? "Ready to find your next worker?"
            : "Let's get you on a job."}
        </p>

        <div className="mt-6 space-y-3">
          {isContractor ? (
            <>
              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="flex flex-col items-center p-4">
                    <span className="text-3xl font-bold">{jobCount}</span>
                    <span className="text-xs text-muted-foreground mt-1">Active Jobs</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex flex-col items-center p-4">
                    <span className="text-3xl font-bold">{matchCount}</span>
                    <span className="text-xs text-muted-foreground mt-1">New Matches</span>
                  </CardContent>
                </Card>
              </div>

              {/* Post a Job CTA */}
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Plus className="h-6 w-6" />
                    Post a Job
                  </CardTitle>
                  <CardDescription className="text-base">
                    AI writes the post. You just tap.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full h-16 text-lg font-semibold">
                    <Link href="/contractor/post-job">Post a Job</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* View Matches */}
              {matchCount > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-6 w-6" />
                      {matchCount} Worker{matchCount > 1 ? "s" : ""} Matched
                    </CardTitle>
                    <CardDescription className="text-base">
                      Workers have applied to your jobs
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="outline" className="w-full h-14 text-base font-semibold">
                      <Link href="/contractor/matches">View Matches</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Company profile */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-6 w-6" />
                    Company Profile
                  </CardTitle>
                  <CardDescription className="text-base">
                    Manage your company details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full h-14 text-base">
                    Edit Profile
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Browse Jobs CTA */}
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Briefcase className="h-6 w-6" />
                    Find Work
                  </CardTitle>
                  <CardDescription className="text-base">
                    Browse available construction jobs near you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full h-16 text-lg font-semibold">
                    <Link href="/worker/jobs">Browse Jobs</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Profile */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <HardHat className="h-6 w-6" />
                    Your Profile
                  </CardTitle>
                  <CardDescription className="text-base">
                    A strong profile gets you more matches
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full h-14 text-base">
                    Edit Profile
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>

      <BottomNavWrapper userType={profile?.type || "worker"} />
    </div>
  );
}
